/**
 * Preferências de tela guardadas no navegador: filtros e configuração de colunas.
 *
 * `localStorage`, não `sessionStorage`: a graça é justamente sobreviver ao fechar a aba.
 * O `sessionStorage` já é onde a sessão do usuário vive (`app.service.ts`) e morre com
 * ela — guardar preferência lá não guardaria nada.
 *
 * Nada aqui é dado de negócio. Se a leitura falhar, a tela cai no padrão e segue: todo
 * acesso é embrulhado porque `localStorage` lança de verdade (modo privado do Safari,
 * cota estourada, storage bloqueado por política), e uma preferência não vale derrubar
 * a tela.
 *
 * ATENÇÃO: o armazenamento é por navegador, não por usuário — dois usuários no mesmo
 * perfil do Chrome compartilham estas preferências. Serve para filtro e coluna, que é o
 * que guardamos aqui; nunca guarde nada que dependa de permissão ou identidade.
 */

/**
 * Todas as chaves em um lugar só, para colisão entre telas ficar visível.
 *
 * `reviewFilters` mantém o nome literal que a tela de review já usava antes deste util:
 * renomear apagaria os filtros que os usuários já têm salvos.
 */
export const UI_PREF_KEYS = {
  reviewFilters: 'smart-review-filters',
  reviewColumns: 'smart-review-columns',
  costsFilters: 'smart-review-costs-filters',
  curationQueueFilters: 'smart-review-curation-queue-filters',
  /** Sufixada pelo status da aba — cada aba da fila tem um conjunto de colunas próprio. */
  curationQueueColumns: 'smart-review-curation-queue-columns',
  curationStatsFilters: 'smart-review-curation-stats-filters',
  reviewStatsFilters: 'smart-review-dashboard-filters'
} as const;

/**
 * Lê uma preferência, passando o conteúdo cru pelo `sanitize` da tela.
 *
 * O `sanitize` é obrigatório, e não um detalhe opcional: o que está no `localStorage` é
 * texto que o usuário pode ter editado à mão, ou que uma versão anterior da tela gravou
 * em outro formato. Sem validar, um `statuses: "done"` (string onde se espera array)
 * quebra a tela na primeira renderização, e o usuário fica preso — a preferência
 * ruim é recarregada a cada F5.
 *
 * Devolve `null` quando não há preferência utilizável, em qualquer um dos casos: chave
 * ausente, storage indisponível, JSON inválido ou conteúdo recusado pelo `sanitize`.
 */
export function readPref<T>(key: string, sanitize: (raw: unknown) => T | null): T | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null;
  }

  if (!raw) { return null; }

  try {
    return sanitize(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Grava a preferência. Falha em silêncio: a tela continua funcionando sem salvar. */
export function writePref(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Sem espaço ou storage bloqueado — nada a fazer, e nada que valha um erro na tela.
  }
}

/**
 * True quando já existe algo salvo nesta chave — mesmo que ilegível.
 *
 * Serve para distinguir "o usuário nunca personalizou esta tela" de "personalizou e o
 * resultado por acaso é igual ao padrão". Quem só quer o valor usa `readPref`.
 */
export function hasPref(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

/** Apaga a preferência — é o que "limpar filtros" e "restaurar padrão" fazem. */
export function clearPref(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Mesmo motivo de `writePref`.
  }
}

/**
 * Só as strings do array — e, quando `allowed` é informado, só as que ainda existem
 * nessa lista. É o que impede um valor de enum removido numa versão anterior de voltar
 * como filtro que o backend não conhece mais.
 */
export function sanitizeStringArray(value: unknown, allowed?: readonly string[]): string[] {
  if (!Array.isArray(value)) { return []; }
  const strings = value.filter((item): item is string => typeof item === 'string');
  return allowed ? strings.filter(item => allowed.includes(item)) : strings;
}

/** A string, ou vazio para qualquer outra coisa. */
export function sanitizeString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
